/**
 * Analytics multiplataforma (una sola propiedad GA4, un stream por plataforma):
 *
 *   - App nativa (Android + iOS) → SDK nativo de Firebase Analytics (plugin
 *     Capacitor, import dinámico). Sesiones/first_open automáticos +
 *     screen_view por navegación (HashRouter → hashchange).
 *   - Web        → gtag.js (stream web G-…), inyectado en runtime SOLO fuera
 *     de la app nativa para no medir doble. page_view inicial automático +
 *     page_view por navegación SPA (BrowserRouter → router.subscribe).
 *
 * GDPR: el banner de consentimiento está pendiente (decisión consciente del
 * usuario); cuando se añada, condicionar la carga de gtag al consentimiento.
 */

import { isNative } from './platform';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let started = false;

export function initAnalytics(): void {
  if (started) return;
  started = true;

  if (isNative()) {
    initNative();
  } else {
    initWeb();
  }
}

// ---------------------------------------------------------------------------
// App nativa (SDK nativo de Firebase, Android + iOS)
// ---------------------------------------------------------------------------

function initNative(): void {
  void (async () => {
    try {
      const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');

      const trackScreen = (): void => {
        // '#/student/dashboard' → '/student/dashboard'
        const path = window.location.hash.replace(/^#/, '') || '/';
        void FirebaseAnalytics.logEvent({
          name: 'screen_view',
          params: { screen_name: path, screen_class: path },
        });
      };

      window.addEventListener('hashchange', trackScreen);
      trackScreen(); // pantalla inicial
    } catch (err) {
      // Analytics nunca debe romper la app.
      console.error('initAnalytics (native) failed:', err);
    }
  })();
}

// ---------------------------------------------------------------------------
// Web (gtag.js)
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function initWeb(): void {
  if (!GA_MEASUREMENT_ID) return;

  try {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
    window.gtag('js', new Date());
    // page_view inicial automático; los de navegación SPA se envían abajo.
    window.gtag('config', GA_MEASUREMENT_ID);

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Navegación SPA (BrowserRouter): un page_view por cambio de ruta.
    void (async () => {
      const { router } = await import('@/router');
      let lastPath = window.location.pathname;
      router.subscribe((state) => {
        const path = state.location.pathname;
        if (path === lastPath) return;
        lastPath = path;
        window.gtag?.('event', 'page_view', {
          page_path: path,
          page_location: window.location.origin + path,
        });
      });
    })();
  } catch (err) {
    console.error('initAnalytics (web) failed:', err);
  }
}
